import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Users,
  ExternalLink,
  LogOut,
  AlertCircle,
  Loader2,
} from "lucide-react";
import ReactPaginate from "react-paginate";
import { useTheme } from "../../../contexts/ThemeContext";
import { API_BASE_URL } from "../../../config";
import Layout from "../../Layout/Layout";
import Loader from "../../utils/Loader";
import Swal from "sweetalert2";

const ListOfAllGroups = () => {
  const { theme } = useTheme();
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(50); // Number of groups per page

  const showNotification = (message, type = "success") => {
    const id = Date.now();
    const notification = { id, message, type };
    setNotifications((prev) => [...prev, notification]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  };

  const leaveGroup = async (groupId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to leave this group?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, leave group!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const token = window.__appSession.getToken();
      if (!token) {
        showNotification(
          "No authentication token found. Please log in.",
          "error",
        );
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/${groupId}/leavegroup`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ groupId }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setGroups((prevGroups) =>
          prevGroups.filter((group) => group.id !== groupId),
        );
        setFilteredGroups((prevGroups) =>
          prevGroups.filter((group) => group.id !== groupId),
        );
        showNotification("Successfully left the group!", "success");
      } else {
        showNotification(
          data.message?.message || "Failed to leave the group.",
          "error",
        );
      }
    } catch (err) {
      console.error("Error leaving group:", err);
      showNotification("An error occurred while leaving the group.", "error");
    }
  };

  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        const token = window.__appSession.getToken();
        if (!token) {
          setError("No authentication token found. Please log in.");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/whatsapp/group-details`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({}),
          },
        );

        const data = await response.json();

        if (
          response.ok &&
          data.groupDetails &&
          Array.isArray(data.groupDetails.result)
        ) {
          setGroups(data.groupDetails.result);
          setFilteredGroups(data.groupDetails.result);
          // Reset to first page when new data is loaded
          setCurrentPage(0);
        } else {
          setError("Failed to fetch group details.");
        }
      } catch (err) {
        console.error("Error fetching group details:", err);
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchGroupDetails();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    const filtered = groups.filter((group) =>
      group.name
        ? group.name.toLowerCase().includes(query.toLowerCase())
        : false,
    );
    setFilteredGroups(filtered);
    setCurrentPage(0); // Reset to first page when searching
  };

  // Calculate paginated groups
  const indexOfLastGroup = (currentPage + 1) * itemsPerPage;
  const indexOfFirstGroup = indexOfLastGroup - itemsPerPage;
  const currentGroups = filteredGroups.slice(
    indexOfFirstGroup,
    indexOfLastGroup,
  );
  const pageCount = Math.ceil(filteredGroups.length / itemsPerPage);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  return (
    <Layout>
      <div>
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`rounded-xl border px-3 py-2 text-xs font-medium shadow-lg backdrop-blur ${
                notification.type === "success"
                  ? "border-emerald-200 bg-emerald-500/95 text-white"
                  : notification.type === "error"
                    ? "border-rose-200 bg-rose-500/95 text-white"
                    : "border-indigo-200 bg-indigo-500/95 text-white"
              }`}
            >
              {notification.message}
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-3 mb-3">
            {/* Header */}
            <div
              className={`glass-surface-strong ${theme.contentBg} ${theme.contentBorder} border rounded-2xl px-3 py-2`}
            >
              <div className="flex items-center gap-2 justify-between">
                <div className="flex items-center space-x-2">
                  <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 p-2 shadow-[0_14px_30px_-22px_rgba(79,70,229,0.9)]">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h1 className="text-base font-semibold text-slate-900">
                      WhatsApp Groups
                    </h1>
                    <p className="text-xs text-slate-500">
                      Browse and manage your WhatsApp groups
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="text-center">
                  <div className="flex items-center space-x-1">
                    <Users className="h-3 w-3 text-indigo-500" />
                    <span className="text-xs font-medium text-slate-900">
                      {filteredGroups.length}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Groups</p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div
              className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl flex w-full items-center px-3 py-2`}
            >
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Search by group name..."
                  className={`w-full rounded-xl border border-indigo-200 bg-white/85 pl-9 pr-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                />
              </div>
            </div>
          </div>

          {/* Groups Content */}
          <div
            className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl overflow-hidden`}
          >
            <div className="p-3 border-b border-indigo-200">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                <Users className="h-4 w-4 mr-2" />
                Groups ({filteredGroups.length})
              </h3>
            </div>

            {loading && <Loader theme={theme} text="Loading groups..." />}

            {error && !loading && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                  <p className="text-xs text-red-600">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && (
              <div
                className="overflow-x-auto max-h-[40rem] overflow-y-auto"
                style={{ scrollbarWidth: "thin" }}
              >
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-100/90 via-violet-100/90 to-indigo-100/90 top-0 sticky z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        #
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Group Name
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Group ID
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-100/80">
                    {currentGroups.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-3 py-6 text-center">
                          <Users className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-xs text-slate-500">
                            No groups found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      currentGroups.map((group, idx) => (
                        <tr
                          key={group.id}
                          className="transition-colors hover:bg-indigo-50/55"
                        >
                          <td className="px-3 py-2 text-xs text-slate-700">
                            {indexOfFirstGroup + 1 + idx}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center">
                              <div className="mr-2 rounded bg-indigo-100 p-1">
                                <Users className="h-3 w-3 text-indigo-600" />
                              </div>
                              <div>
                                <div className="text-xs font-medium text-slate-900">
                                  {group.name || "Unnamed Group"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-700">
                            <code className="rounded border border-indigo-200 bg-indigo-50 px-1 py-0.5 text-xs text-indigo-700">
                              {group.id}
                            </code>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center space-x-4">
                              <Link
                                to={`/group/${group.id}`}
                                className="flex items-center rounded-lg border border-indigo-200 bg-indigo-50 p-1.5 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-100 hover:opacity-95"
                              >
                                <ExternalLink className="h-3 w-3 hover:scale-110 transition-all duration-200 ease-linear" />
                                {/* <span>View</span> */}
                              </Link>
                              <button
                                onClick={() => leaveGroup(group.id)}
                                className="flex items-center rounded-lg border border-rose-200 bg-rose-50 p-1.5 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-100"
                              >
                                <LogOut className="h-3 w-3 hover:scale-110 transition-all duration-200 ease-linear" />
                                {/* <span>Leave</span> */}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Table Footer */}
            {!loading && !error && (
              <div className="border-t border-indigo-200 bg-indigo-50/40 px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-700">
                    Showing {indexOfFirstGroup + 1} to{" "}
                    {Math.min(indexOfLastGroup, filteredGroups.length)} of{" "}
                    {filteredGroups.length} groups
                  </div>
                  <div className="text-xs text-slate-500">
                    Total: {groups.length} groups
                  </div>
                  {/* Pagination */}
                  {!loading && !error && filteredGroups.length > 0 && (
                    <div className="">
                      <ReactPaginate
                        previousLabel={"Previous"}
                        nextLabel={"Next"}
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={3}
                        onPageChange={handlePageClick}
                        containerClassName={
                          "flex items-center justify-center space-x-2"
                        }
                        pageClassName="rounded-lg border border-indigo-200 bg-white px-3 py-1 text-xs font-medium transition-colors hover:bg-indigo-50"
                        pageLinkClassName="text-slate-700"
                        previousClassName="rounded-lg border border-indigo-200 bg-white px-3 py-1 text-xs font-medium transition-colors hover:bg-indigo-50"
                        previousLinkClassName="text-slate-700"
                        nextClassName="rounded-lg border border-indigo-200 bg-white px-3 py-1 text-xs font-medium transition-colors hover:bg-indigo-50"
                        nextLinkClassName="text-slate-700"
                        breakClassName="px-3 py-1 text-xs text-slate-500"
                        activeClassName="!border-indigo-600 !bg-indigo-600 text-white"
                        disabledClassName={"opacity-50 cursor-not-allowed"}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ListOfAllGroups;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import { motion } from "framer-motion";
// import { API_BASE_URL } from "../../../config";
// import Layout from "../../Layout/Layout";

// const Footer = () => {
//   return (
//     <footer className="bg-gradient-to-r from-green-600 to-teal-500 text-white py-4 text-center shadow-inner">
//       <p className="text-sm tracking-wide">
//         Â© {new Date().getFullYear()} Smart ItBox. All rights reserved.
//       </p>
//     </footer>
//   );
// };

// const ListOfAllGroups = () => {
//   const [groups, setGroups] = useState([]);
//   const [filteredGroups, setFilteredGroups] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [searchQuery, setSearchQuery] = useState("");

//   const leaveGroup = async (groupId) => {
//     try {
//       const token = window.__appSession.getToken();
//       if (!token) {
//         setError("No authentication token found. Please log in.");
//         return;
//       }

//       const response = await fetch(
//         `${API_BASE_URL}/api/whatsapp/${groupId}/leavegroup`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//           body: JSON.stringify({ groupId }),
//         }
//       );

//       const data = await response.json();

//       if (response.ok) {
//         setGroups((prevGroups) =>
//           prevGroups.filter((group) => group.id !== groupId)
//         );
//         setFilteredGroups((prevGroups) =>
//           prevGroups.filter((group) => group.id !== groupId)
//         );
//       } else {
//         // Extract the message string from data.message if it exists
//         setError(data.message?.message || "Failed to leave the group.");
//       }
//     } catch (err) {
//       console.error("Error leaving group:", err);
//       setError("An error occurred while leaving the group.");
//     }
//   };

//   useEffect(() => {
//     const fetchGroupDetails = async () => {
//       try {
//         const token = window.__appSession.getToken();
//         if (!token) {
//           setError("No authentication token found. Please log in.");
//           setLoading(false);
//           return;
//         }

//         const response = await fetch(
//           `${API_BASE_URL}/api/whatsapp/group-details`,
//           {
//             method: "POST",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//             },
//             body: JSON.stringify({}),
//           }
//         );

//         const data = await response.json();
//         console.log("API Response:", data);

//         if (
//           response.ok &&
//           data.groupDetails &&
//           Array.isArray(data.groupDetails.result)
//         ) {
//           setGroups(data.groupDetails.result);
//           setFilteredGroups(data.groupDetails.result);
//         } else {
//           // Extract the message string from data.message if it exists
//           setError(data.message?.message || "Failed to fetch group details.");
//         }
//       } catch (err) {
//         console.error("Error fetching group details:", err);
//         setError("An error occurred while fetching data.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchGroupDetails();
//   }, []);

//   const handleSearch = (e) => {
//     const query = e.target.value;
//     setSearchQuery(query);
//     const filtered = groups.filter((group) =>
//       group.name
//         ? group.name.toLowerCase().includes(query.toLowerCase())
//         : false
//     );
//     setFilteredGroups(filtered);
//   };

//   return (
//     <Layout>
//       <motion.main
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         className="flex-grow w-full min-h-[calc(100vh-4rem)] py-6 px-4 sm:px-6"
//       >
//         <div className="bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-6 sm:p-8 w-full max-w-4xl mx-auto border border-gray-200 transition-all">
//           <header className="text-center mb-6">
//             <h1 className="text-3xl sm:text-4xl font-bold text-[#25D366] tracking-tight font-serif">
//               WhatsApp Group Details
//             </h1>
//             <p className="text-sm sm:text-base text-gray-600 mt-2 font-medium">
//               Browse and manage your WhatsApp groups with ease
//             </p>
//           </header>

//           <motion.div whileFocus={{ scale: 1.02 }} className="relative mb-6">
//             <input
//               type="text"
//               value={searchQuery}
//               onChange={handleSearch}
//               placeholder="Search by group name..."
//               className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//             />
//             <svg
//               className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500"
//               xmlns="http://www.w3.org/2000/svg"
//               fill="none"
//               viewBox="0 0 24 24"
//               stroke="currentColor"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth="2"
//                 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
//               />
//             </svg>
//           </motion.div>

//           <h2 className="text-lg sm:text-xl font-semibold mb-6 text-gray-800 tracking-wide text-center">
//             Total Groups: {filteredGroups.length}
//           </h2>

//           {loading && (
//             <motion.div
//               className="text-center py-6"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               exit={{ opacity: 0 }}
//             >
//               <svg
//                 className="animate-spin h-12 w-12 text-[#25D366] mx-auto mb-4"
//                 xmlns="http://www.w3.org/2000/svg"
//                 viewBox="0 0 24 24"
//               >
//                 <circle
//                   className="opacity-25"
//                   cx="12"
//                   cy="12"
//                   r="10"
//                   stroke="currentColor"
//                   strokeWidth="4"
//                 />
//                 <path
//                   className="opacity-75"
//                   fill="currentColor"
//                   d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"
//                 />
//               </svg>
//               <p className="text-lg text-gray-600">Loading...</p>
//             </motion.div>
//           )}

//           {error && !loading && (
//             <motion.div
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5 }}
//               className="text-center py-6 text-red-600"
//             >
//               <p className="text-lg">{error}</p>
//             </motion.div>
//           )}

//           {!loading && !error && (
//             <div className="space-y-4 max-h-[600px] overflow-y-auto">
//               {filteredGroups?.length > 0 ? (
//                 filteredGroups?.map((group) => (
//                   <motion.div
//                     key={group.id}
//                     initial={{ opacity: 0, y: 10 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.3 }}
//                     className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-gray-50/80 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-gray-200 hover:scale-[1.01] hover:shadow-md transition-all"
//                   >
//                     <Link
//                       to={`/group/${group.id}`}
//                       className="block flex-grow mb-4 sm:mb-0"
//                     >
//                       <h2 className="text-lg sm:text-xl font-semibold text-indigo-700 hover:text-indigo-900 transition-all">
//                         {group.name || "Unnamed Group"}
//                       </h2>
//                       <p className="text-sm text-gray-600">
//                         Group ID: {group.id}
//                       </p>
//                     </Link>
//                     <motion.button
//                       whileHover={{
//                         scale: 1.05,
//                         boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
//                       }}
//                       whileTap={{ scale: 0.95 }}
//                       onClick={() => leaveGroup(group.id)}
//                       className="bg-red-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-red-600 transition-all flex items-center space-x-2"
//                       title="Leave Group"
//                     >
//                       <span className="text-sm font-semibold">Leave</span>
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                         stroke="currentColor"
//                         className="w-5 h-5"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth="2"
//                           d="M14 5l7 7m0 0l-7 7m7-7H3"
//                         />
//                       </svg>
//                     </motion.button>
//                   </motion.div>
//                 ))
//               ) : (
//                 <p className="text-center text-lg text-gray-600">
//                   No groups found
//                 </p>
//               )}
//             </div>
//           )}
//         </div>
//       </motion.main>
//       <Footer />
//     </Layout>
//   );
// };

// export default ListOfAllGroups;
